package org.teddapp.project.ui.objectives

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import org.teddapp.project.db.TaskRecurrence
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Task
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddDurationChips
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Modification d'une micro-tâche : son intitulé, sa durée et son échéance. Ouvert depuis le
 * crayon de la ligne, dans une étape comme dans un projet.
 */
@Composable
internal fun MicroTaskEditor(task: Task, repos: TeddRepositories, onClose: () -> Unit) {
    val scope = rememberCoroutineScope()
    var title by remember(task.id) { mutableStateOf(task.title) }
    var minutes by remember(task.id) { mutableStateOf(task.estimated_minutes?.toInt()) }
    var due by remember(task.id) { mutableStateOf(task.due_date ?: "") }
    var recurrence by remember(task.id) { mutableStateOf(TaskRecurrence.of(task.recurrence)) }

    fun save() {
        val trimmed = title.trim()
        if (trimmed.isEmpty()) return
        scope.launch {
            repos.tasks.update(
                id = task.id,
                parentKind = ParentKind.entries.firstOrNull { it.raw == task.parent_kind } ?: ParentKind.Standalone,
                parentId = task.parent_id,
                statusId = task.status_id,
                title = trimmed,
                description = task.description,
                status = task.status,
                priority = task.priority,
                effort = task.effort,
                dueDate = due.ifBlank { null },
                estimatedMinutes = minutes?.toLong(),
                realMinutes = task.real_minutes,
                recurrence = if (recurrence == TaskRecurrence.None) null else recurrence.key,
                pomodoroPoolDate = task.pomodoro_pool_date,
                orderIndex = task.order_index,
                completedAt = task.completed_at,
            )
        }
        onClose()
    }

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(440.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text("Modifier la micro-tâche", style = TeddType.Title2, color = TeddTokens.Ink)
            Column {
                Text("INTITULÉ", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 44.dp)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                        .padding(horizontal = 12.dp, vertical = 12.dp),
                    contentAlignment = Alignment.CenterStart,
                ) {
                    BasicTextField(
                        value = title,
                        onValueChange = { title = it },
                        textStyle = TeddType.Body.copy(color = TeddTokens.Ink, fontSize = 14.sp),
                        cursorBrush = SolidColor(TeddTokens.LimeStrong),
                        modifier = Modifier.fillMaxWidth(),
                        decorationBox = { inner ->
                            if (title.isEmpty()) Text("Un pas de 5 à 10 minutes…", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                            inner()
                        },
                    )
                }
            }
            Column {
                Text("DURÉE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                TeddDurationChips(value = minutes, onChange = { minutes = it })
            }
            TeddDateField(label = "Échéance", value = due, onValueChange = { due = it })
            Column {
                Text("RÉPÉTITION", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 2.dp))
                Text(
                    text = "Une tâche qui se répète revient d'elle-même à la date suivante quand tu la coches.",
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    modifier = Modifier.padding(bottom = 6.dp),
                )
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    TaskRecurrence.entries.forEach { r ->
                        val on = recurrence == r
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(TeddTokens.RFull))
                                .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { recurrence = r }
                                .padding(horizontal = 11.dp, vertical = 6.dp),
                        ) {
                            Text(
                                text = r.label,
                                color = if (on) TeddTokens.OnAction else TeddTokens.Ink,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold,
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(2.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
                TeddButton(
                    text = "Enregistrer",
                    onClick = { save() },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    enabled = title.isNotBlank(),
                )
            }
        }
    }
}
